import { DelayData } from "pages/FlowBuilderv2/Nodes/NodeData";
import React, { FC } from "react";
import { useAppSelector } from "store/hooks";
import onboardingCursorImage from "../../../Onboardingv2/svg/onboarding-cursor.svg";

interface TimeDelayEditorProps {
  delay: DelayData;
  onChange: (delay: DelayData) => void;
}

const TimeDelayEditor: FC<TimeDelayEditorProps> = ({ delay, onChange }) => {
  const { isOnboarding, isOnboardingWaitUntilTimeSettingTooltipVisible } =
    useAppSelector((store) => store.flowBuilder);
  const businessHours = delay.businessHours || {
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: "09:00",
    endTime: "17:00",
  };
  const businessHoursOnly =
    delay.businessHoursOnly === true || (delay.businessDays || 0) > 0;
  const patchDelay = (patch: Partial<DelayData>) =>
    onChange({ ...delay, ...patch });

  return (
    <div className="relative flex flex-col gap-3 w-full mt-[1px] p-[1px]">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 w-full">
        <div className="font-inter font-normal text-[14px] leading-[22px]">
          Wait
        </div>

        <input
          type="number"
          min={0}
          value={delay.days || undefined}
          onChange={(e) =>
            +e.target.value >= 0 && patchDelay({ days: +e.target.value })
          }
          placeholder="Days"
          className="w-full px-[12px] py-[5px] font-inter font-normal text-[14px] leading-[22px] border border-themed-border placeholder:font-inter placeholder:font-normal placeholder:text-[14px] placeholder:leading-[22px] placeholder:text-themed-textSecondary rounded-sm"
        />

        <div>:</div>

        <input
          type="number"
          min={0}
          value={delay.hours || undefined}
          onChange={(e) =>
            +e.target.value >= 0 && patchDelay({ hours: +e.target.value })
          }
          placeholder="Hours"
          className="w-full px-[12px] py-[5px] font-inter font-normal text-[14px] leading-[22px] border border-themed-border placeholder:font-inter placeholder:font-normal placeholder:text-[14px] placeholder:leading-[22px] placeholder:text-themed-textSecondary rounded-sm"
        />

        <div>:</div>

        <input
          type="number"
          min={0}
          value={delay.minutes || undefined}
          onChange={(e) =>
            +e.target.value >= 0 && patchDelay({ minutes: +e.target.value })
          }
          placeholder="Mins"
          className="w-full px-[12px] py-[5px] font-inter font-normal text-[14px] leading-[22px] border border-themed-border placeholder:font-inter placeholder:font-normal placeholder:text-[14px] placeholder:leading-[22px] placeholder:text-themed-textSecondary rounded-sm"
        />

        <div>:</div>

        <input
          type="number"
          min={0}
          max={59}
          value={delay.seconds || undefined}
          onChange={(e) => {
            const seconds = Math.min(59, Math.max(0, +e.target.value || 0));
            patchDelay({ seconds });
          }}
          placeholder="Secs"
          className="w-full px-[12px] py-[5px] font-inter font-normal text-[14px] leading-[22px] border border-themed-border placeholder:font-inter placeholder:font-normal placeholder:text-[14px] placeholder:leading-[22px] placeholder:text-themed-textSecondary rounded-sm"
        />
      </div>

      <div className="grid grid-cols-[minmax(120px,1fr)_auto] gap-2 items-end">
        <div>
          <div className="text-[12px] text-themed-textSecondary mb-1">
            Business days
          </div>
          <input
            type="number"
            min={0}
            value={delay.businessDays || undefined}
            onChange={(e) => {
              const businessDays = Math.max(0, Number(e.target.value) || 0);
              patchDelay({
                businessDays,
                businessHoursOnly: businessDays > 0 || delay.businessHoursOnly,
                businessHours,
                timezone: delay.timezone || "UTC",
              });
            }}
            placeholder="0"
            className="w-full px-[12px] py-[5px] font-inter font-normal text-[14px] leading-[22px] border border-themed-border rounded-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-[13px] leading-[20px] pb-[6px]">
          <input
            type="checkbox"
            checked={businessHoursOnly}
            onChange={(e) =>
              patchDelay({
                businessHoursOnly: e.target.checked,
                businessHours,
                timezone: delay.timezone || "UTC",
              })
            }
          />
          Business hours
        </label>
      </div>

      {businessHoursOnly && (
        <div className="flex flex-col gap-2 rounded-sm border border-themed-border bg-themed-card p-2">
          <div className="flex gap-1">
            {[
              ["M", 1],
              ["T", 2],
              ["W", 3],
              ["T", 4],
              ["F", 5],
              ["S", 6],
              ["S", 7],
            ].map(([label, day]) => {
              const isChecked = businessHours.daysOfWeek?.includes(
                day as number
              );
              return (
                <button
                  type="button"
                  key={day}
                  className={`h-8 flex-1 border border-themed-border text-[12px] ${
                    isChecked
                      ? "bg-[#6366F1] text-white"
                      : "bg-themed-background text-themed-text"
                  }`}
                  onClick={() => {
                    const daysOfWeek = isChecked
                      ? businessHours.daysOfWeek?.filter((item) => item !== day)
                      : [...(businessHours.daysOfWeek || []), day as number];
                    patchDelay({
                      businessHours: {
                        ...businessHours,
                        daysOfWeek,
                      },
                    });
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="time"
              value={businessHours.startTime}
              onChange={(e) =>
                patchDelay({
                  businessHours: {
                    ...businessHours,
                    startTime: e.target.value || "09:00",
                  },
                })
              }
              className="w-full px-[10px] py-[5px] border border-themed-border rounded-sm text-[13px]"
            />
            <input
              type="time"
              value={businessHours.endTime}
              onChange={(e) =>
                patchDelay({
                  businessHours: {
                    ...businessHours,
                    endTime: e.target.value || "17:00",
                  },
                })
              }
              className="w-full px-[10px] py-[5px] border border-themed-border rounded-sm text-[13px]"
            />
            <input
              value={delay.timezone || "UTC"}
              onChange={(e) =>
                patchDelay({ timezone: e.target.value || "UTC" })
              }
              placeholder="Timezone"
              className="w-full px-[10px] py-[5px] border border-themed-border rounded-sm text-[13px]"
            />
          </div>
        </div>
      )}
      {isOnboarding && isOnboardingWaitUntilTimeSettingTooltipVisible && (
        <>
          <div className="absolute w-full bottom-[-60px] left-0 p-[10px] bg-themed-foreground text-themed-background font-medium">
            Add 1 hour to Time Delay trigger
          </div>
          <img
            className="absolute pointer-events-none -bottom-[40%] left-[65%] -translate-x-1/2 "
            src={onboardingCursorImage}
            alt=""
          />
        </>
      )}
    </div>
  );
};

export default TimeDelayEditor;
